import { useEffect, useState } from 'react'
import type { AnswerValue, PhaseInfo, WorkflowStage } from '../../types/workflow'

interface GoalsTemplateProps {
  stage: WorkflowStage
  initialValue?: string[]
  onSelect: (value: AnswerValue) => void
  currentPhase?: PhaseInfo | null
}

const GOAL_OPTIONS = [
  { id: 'lose_weight', label: 'Lose weight', symbol: '\u2696\ufe0f' },
  { id: 'more_energy', label: 'Feel more energy', symbol: '\u26a1' },
  { id: 'health_markers', label: 'Improve health markers', symbol: '\ud83d\udcca' },
  { id: 'confidence', label: 'Build confidence', symbol: '\ud83d\udcaa' },
  { id: 'mobility', label: 'Better mobility', symbol: '\ud83c\udfc3' },
  { id: 'sleep', label: 'Sleep better', symbol: '\ud83d\ude34' },
]

export default function GoalsTemplate({ stage, initialValue, onSelect, currentPhase }: GoalsTemplateProps) {
  const [selectedGoals, setSelectedGoals] = useState<string[]>(Array.isArray(initialValue) ? initialValue : [])

  useEffect(() => {
    setSelectedGoals(Array.isArray(initialValue) ? initialValue : [])
  }, [initialValue])

  const handleContinue = () => {
    if (!selectedGoals.length) return
    onSelect({
      primary_goal: selectedGoals[0],
      goals_selected: selectedGoals,
      goals_selected_labels: GOAL_OPTIONS.filter((goal) => selectedGoals.includes(goal.id)).map((goal) => goal.label),
      stage_id: stage.id,
    })
  }

  return (
    <div className="flex-1 flex flex-col gap-4">
      <div className="grid grid-cols-2 gap-2">
        {GOAL_OPTIONS.map((goal) => {
          const isSelected = selectedGoals.includes(goal.id)
          return (
            <button
              key={goal.id}
              onClick={() =>
                setSelectedGoals((prev) =>
                  prev.includes(goal.id) ? prev.filter((id) => id !== goal.id) : [...prev, goal.id]
                )
              }
              className={`flex flex-col items-center gap-2 py-5 px-3 text-[14px] font-medium text-center rounded-2xl cursor-pointer transition-all duration-300 border glass hover:border-[var(--ww-border-hover)] hover:-translate-y-px ${
                isSelected
                  ? 'border-[var(--ww-accent)] choice-selected text-[var(--ww-text)] selected-glow'
                  : 'border-[var(--ww-glass-border)] bg-[var(--ww-surface)] text-[var(--ww-text-2)]'
              }`}
              type="button"
            >
              <span className="text-2xl">{goal.symbol}</span>
              <span>{goal.label}</span>
            </button>
          )
        })}
      </div>

      <div className="mt-auto pt-6 pb-2 -ml-[42px] w-[calc(100%+42px)]">
        <button
          onClick={handleContinue}
          disabled={!selectedGoals.length}
          className="w-full py-4 px-6 text-[15px] font-semibold text-[var(--ww-btn-text)] bg-[var(--ww-btn-bg)] border-none rounded-full cursor-pointer transition-all duration-300 shadow-[0_2px_8px_rgba(0,0,0,0.1)] hover:not-disabled:bg-[var(--ww-btn-hover)] hover:not-disabled:-translate-y-0.5 hover:not-disabled:shadow-[0_8px_24px_rgba(0,0,0,0.15)] active:not-disabled:translate-y-0 disabled:opacity-25 disabled:cursor-default"
        >
          Continue
        </button>
      </div>
    </div>
  )
}
